import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, DOMAIN_COLORS } from "@/lib/api";
import { toast } from "sonner";
import { useEffect } from "react";

export default function TakeTest() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [testId, setTestId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [institutionCode, setInstitutionCode] = useState("");
  const [program, setProgram] = useState("");
  const [consent, setConsent] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/domains").then((r) => setDomains(r.data)).catch(() => {});
  }, []);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/test/request-otp", { test_id: testId.trim(), email: email.trim(), name });
      toast.success("OTP sent to your email.");
      if (r.data.dev_otp) {
        setDevOtp(r.data.dev_otp);
        toast.info(`Dev OTP: ${r.data.dev_otp}`);
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP.");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/test/verify-otp", { test_id: testId.trim(), email: email.trim(), otp: otp.trim() });
      toast.success("Email verified.");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "OTP verification failed.");
    } finally { setLoading(false); }
  };

  const toggleDomain = (code) => {
    setSelectedDomains((p) => p.includes(code) ? p.filter((c) => c !== code) : [...p, code]);
  };

  const startTest = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please accept consent to proceed."); return; }
    if (selectedDomains.length < 3 || selectedDomains.length > 9) {
      toast.error("Select between 3 and 9 domains."); return;
    }
    setLoading(true);
    try {
      const r = await api.post("/test/start", {
        test_id: testId.trim(), email: email.trim(), name,
        institution_code: institutionCode || null, program: program || null,
        consent, domains_selected: selectedDomains,
      });
      sessionStorage.setItem("msis_session", JSON.stringify({
        test_id: testId.trim(), email: email.trim(), name, items: r.data.items,
        responses: r.data.responses, current_index: r.data.current_index,
        domains: selectedDomains,
      }));
      nav("/test");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start test.");
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <section className="container-msis py-10 max-w-2xl">
        <div className="overline mb-2">Take the Test</div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Begin your MSIS assessment.</h1>

        <Stepper step={step} />

        {step === 1 && (
          <form onSubmit={requestOtp} className="mt-8 space-y-4" data-testid="step1-form">
            <div><Label htmlFor="testid" className="text-xs">Test ID</Label>
              <Input id="testid" data-testid="testid-input" placeholder="MSIS-XXX-2026-XXXXXX" value={testId} onChange={e => setTestId(e.target.value)} required className="font-mono" /></div>
            <div><Label htmlFor="email" className="text-xs">Email</Label>
              <Input id="email" data-testid="email-input" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required /></div>
            <div><Label htmlFor="name" className="text-xs">Full Name</Label>
              <Input id="name" data-testid="name-input" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} required /></div>
            <button className="btn-primary" disabled={loading} data-testid="send-otp-btn">{loading ? "Sending…" : "Send OTP"}</button>
            <p className="text-xs text-slate2 pt-2">
              Don&apos;t have a Test ID? <Link to="/pricing" className="underline hover:text-ink" data-testid="buy-test-link">Buy one here</Link>.
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyOtp} className="mt-8 space-y-4" data-testid="step2-form">
            <div><Label htmlFor="otp" className="text-xs">6-digit OTP sent to {email}</Label>
              <Input id="otp" data-testid="otp-input" placeholder="123456" value={otp} onChange={e => setOtp(e.target.value)} required maxLength={6} className="font-mono tracking-widest text-lg" /></div>
            {devOtp && <div className="text-xs text-slate2">Dev mode OTP: <span className="font-mono">{devOtp}</span></div>}
            <div className="flex gap-3">
              <button className="btn-primary" disabled={loading} data-testid="verify-otp-btn">{loading ? "Verifying…" : "Verify OTP"}</button>
              <button type="button" className="btn-ghost" onClick={() => setStep(1)}>Back</button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={startTest} className="mt-8 space-y-5" data-testid="step3-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Institution Code (optional)</Label>
                <Input data-testid="institution-code-input" placeholder="e.g. INST-XXXXX" value={institutionCode} onChange={e => setInstitutionCode(e.target.value)} className="font-mono" /></div>
              <div><Label className="text-xs">Program / Batch (optional)</Label>
                <Input data-testid="program-input" placeholder="e.g. MBA 2026" value={program} onChange={e => setProgram(e.target.value)} /></div>
            </div>

            <div>
              <Label className="block mb-2 text-xs">Select 3 – 9 specialization domains</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="domain-select-grid">
                {domains.map((d) => {
                  const sel = selectedDomains.includes(d.code);
                  return (
                    <button type="button" key={d.code}
                      data-testid={`domain-${d.code}-toggle`}
                      onClick={() => toggleDomain(d.code)}
                      className={`text-left p-2.5 border rounded-md transition-colors ${sel ? "bg-navy/5 border-navy" : "bg-white border-border hover:border-navy/40"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-[10px]" style={{ color: DOMAIN_COLORS[d.code] }}>{d.code}</span>
                        <span className={`w-4 h-4 border rounded ${sel ? "bg-navy border-navy" : "border-border"}`}>
                          {sel && <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                        </span>
                      </div>
                      <div className="font-display font-semibold text-sm mt-1 leading-tight">{d.name}</div>
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-slate2 mt-2">Selected: <span className="font-mono" data-testid="domain-count">{selectedDomains.length}</span> · Items: <span className="font-mono">{selectedDomains.length * 25}</span></div>
            </div>

            <div className="flex items-start gap-3" data-testid="consent-row">
              <Checkbox id="consent" checked={consent} onCheckedChange={setConsent} data-testid="consent-checkbox" />
              <Label htmlFor="consent" className="text-xs text-slate2 leading-relaxed">I consent to the processing of my responses for generating my report and (if institutional) sharing it with my institution. I understand the privacy policy.</Label>
            </div>

            <button className="btn-primary" disabled={loading} data-testid="start-test-btn">{loading ? "Starting…" : "Continue to Instructions"}</button>
          </form>
        )}
      </section>
    </Layout>
  );
}

function Stepper({ step }) {
  const items = [["1", "Verify"], ["2", "OTP"], ["3", "Profile"]];
  return (
    <div className="mt-5 flex items-center gap-2">
      {items.map(([n, l], i) => (
        <div key={n} className="flex items-center gap-2">
          <div className={`w-5 h-5 flex items-center justify-center text-[10px] font-mono rounded-full ${step >= i + 1 ? "bg-navy text-white" : "bg-subtle text-slate2"}`}>{n}</div>
          <span className={`text-xs ${step === i + 1 ? "text-ink font-medium" : "text-slate2"}`}>{l}</span>
          {i < items.length - 1 && <div className="w-6 h-px bg-border" />}
        </div>
      ))}
    </div>
  );
}
